import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../../../components/common/Button/Button';
import Checkbox from '../../../../components/common/Checkbox/Checkbox';
import EmptyState from '../../../../components/common/EmptyState/EmptyState';
import ExerciseThumbnailImage from '../../../../components/common/ExerciseThumbnailImage/ExerciseThumbnailImage';
import Icon from '../../../../components/common/Icon/Icon';
import Loading from '../../../../components/common/Loading/Loading';
import { ExerciseService } from '../../../../services/ExerciseService';
import type { ExerciseItem } from '../../../../types/exercise';
import ExerciseYouTubePlayer from '../ExerciseYouTubePlayer';
import { useEscapeToClose } from '../../../../hooks/useEscapeToClose';
import styles from './ExerciseSwapModal.module.css';

interface ExerciseSwapModalProps {
  open: boolean;
  currentExerciseId: number | null;
  currentExerciseName: string;
  onCancel: () => void;
  onConfirm: (exercise: ExerciseItem, replaceInEntirePlan: boolean) => void;
}

const SWAP_PAGE_SIZE = 100;

const ExerciseSwapModal: React.FC<ExerciseSwapModalProps> = ({
  open,
  currentExerciseId,
  currentExerciseName,
  onCancel,
  onConfirm,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [options, setOptions] = useState<ExerciseItem[]>([]);
  const [selectedExerciseId, setSelectedExerciseId] = useState<number | null>(null);
  const [replaceInEntirePlan, setReplaceInEntirePlan] = useState(false);

  useEffect(() => {
    if (!open || currentExerciseId == null) {
      setOptions([]);
      setSelectedExerciseId(null);
      setReplaceInEntirePlan(false);
      setLoadError('');
      setIsLoading(false);
      return undefined;
    }

    let isMounted = true;
    setIsLoading(true);
    setLoadError('');
    setReplaceInEntirePlan(false);

    const loadOptions = async () => {
      const muscleGroupIds = await ExerciseService.getExerciseMuscleGroupIds(currentExerciseId);

      if (muscleGroupIds.length === 0) {
        if (isMounted) {
          setOptions([]);
          setSelectedExerciseId(null);
        }
        return;
      }

      const response = await ExerciseService.listPaginated(1, SWAP_PAGE_SIZE, {
        groupIds: muscleGroupIds.map(String),
      });
      const nextOptions = response.data
        .filter((exercise) => exercise.id !== currentExerciseId)
        .sort((left, right) => left.name.localeCompare(right.name, 'pt-BR'));

      if (!isMounted) {
        return;
      }

      setOptions(nextOptions);
      setSelectedExerciseId(nextOptions[0]?.id ?? null);
    };

    loadOptions()
      .catch(() => {
        if (isMounted) {
          setLoadError('Não foi possível carregar exercícios do mesmo grupo muscular.');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [currentExerciseId, open]);

  useEscapeToClose(onCancel, open);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const selectedExercise = useMemo(
    () => options.find((exercise) => exercise.id === selectedExerciseId) ?? null,
    [options, selectedExerciseId],
  );

  if (!open) {
    return null;
  }

  return (
    <div className={styles.overlay} onClick={onCancel} role="presentation">
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="exercise-swap-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.header}>
          <div>
            <h2 id="exercise-swap-title" className={styles.title}>Trocar exercício</h2>
            <p className={styles.subtitle}>Substituindo {currentExerciseName}</p>
          </div>
          <button type="button" className={styles.closeButton} onClick={onCancel} aria-label="Fechar troca de exercício">
            <Icon name="close" size={20} />
          </button>
        </div>

        <p className={styles.description}>
          Os exercícios sugeridos ativam o mesmo grupo muscular. A execução atual é mantida; só a referência do exercício muda.
        </p>

        {isLoading ? (
          <div className={styles.state}><Loading inline /></div>
        ) : loadError ? (
          <div className={styles.state}>{loadError}</div>
        ) : options.length === 0 ? (
          <EmptyState
            icon="change_circle"
            showImage={false}
            message="Não há outros exercícios cadastrados no mesmo grupo muscular."
          />
        ) : (
          <div className={styles.content}>
            <div className={styles.playerPane}>
              <ExerciseYouTubePlayer
                videoUrl={selectedExercise?.videoUrl}
                title={`Vídeo de ${selectedExercise?.name ?? 'exercício selecionado'}`}
                emptyMessage="Este exercício não tem vídeo."
              />
              <strong className={styles.selectedName}>{selectedExercise?.name}</strong>
            </div>

            <div className={styles.list} role="listbox" aria-label="Exercícios do mesmo grupo muscular">
              {options.map((exercise) => {
                const isSelected = exercise.id === selectedExerciseId;

                return (
                  <button
                    key={exercise.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`${styles.option}${isSelected ? ` ${styles.optionSelected}` : ''}`}
                    onClick={() => setSelectedExerciseId(exercise.id)}
                  >
                    <ExerciseThumbnailImage
                      exerciseId={exercise.id}
                      exerciseName={exercise.name}
                      alt=""
                      className={styles.optionThumb}
                    />
                    <span className={styles.optionName}>{exercise.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className={styles.footer}>
          <Checkbox
            variant="classic"
            compact
            checked={replaceInEntirePlan}
            onPress={() => setReplaceInEntirePlan((current) => !current)}
            label="Substituir exercício em todo o plano de treino"
          />

          <div className={styles.actions}>
            <Button type="button" variant="outline" size="small" onClick={onCancel}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="primary"
              size="small"
              icon="change_circle"
              disabled={!selectedExercise}
              onClick={() => selectedExercise && onConfirm(selectedExercise, replaceInEntirePlan)}
            >
              Substituir
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExerciseSwapModal;
